function isNonEmptyStringArray(value: unknown): value is string {
	return typeof value === 'string' && value.trim().length > 0;
}

/**
 * Parses a value into an array of label strings.
 *
 * - If the input is an array, it filters out non-string or empty string elements.
 * - If the input is a string:
 *   - Trims whitespace.
 *   - If it looks like a JSON array, attempts to parse and filter it.
 *   - If it contains commas, splits into multiple labels.
 *   - Otherwise, treats it as a single label.
 * - Returns `null` if no valid labels are found.
 *
 * @param value - The value to parse, which can be an array, string, or any type.
 * @returns An array of non-empty label strings, or `null` if none are found.
 */
export function parseLabels(value: unknown): string[] | null {
	if (Array.isArray(value)) {
		const filtered = value.filter(isNonEmptyStringArray);
		return filtered.length ? filtered : null;
	}

	if (typeof value === 'string') {
		const trimmed = value.trim();
		if (!trimmed) {
			return null;
		}
		// JSON-looking input: if it starts with '[' we only accept it when it also ends with ']'.
		if (trimmed.startsWith('[')) {
			if (!trimmed.endsWith(']')) {
				return null;
			}
			try {
				const parsed = JSON.parse(trimmed);
				if (Array.isArray(parsed)) {
					const filtered = parsed.filter(isNonEmptyStringArray);
					return filtered.length ? filtered : null;
				}
				return null;
			} catch {
				return null;
			}
		}
		// If it is a single comma-separated string, split into labels (fallback behavior)
		if (trimmed.includes(',')) {
			const parts = trimmed
				.split(',')
				.map((s) => s.trim())
				.filter(Boolean);
			return parts.length ? parts : null;
		}
		// Treat any single non-empty string as one label
		return [trimmed];
	}

	return null;
}
